import React, { useCallback, createContext, useContext } from 'react';
import { ModelUtils, TemplatingItem } from '../models';
import { apiHooks } from '../../../hooks/useRestAPIHooks';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useEventHook } from '../../../hooks/useEventHook';
import { TemplatingItemDetailRawData } from '../types';

type SelectedTemplatingItemContextType = {
  selectedTemplatingItemId: string | null;
  setSelectedTemplatingItemId: (id: string | null) => void;
};

export const SELECTED_TEMPLATING_ITEM_QUERY_KEY = 'GET /v1/ai-templates/:id';

export const SelectedTemplatingItemContext =
  createContext<SelectedTemplatingItemContextType>({
    selectedTemplatingItemId: null,
    setSelectedTemplatingItemId: () => {
      void 0;
    },
  });

type SelectedTemplatingItemProviderProps = {
  children: React.ReactNode;
  selectedTemplatingItemId: string | null;
  setSelectedTemplatingItemId: (id: string | null) => void;
};

export const SelectedTemplatingItemProvider = ({
  children,
  selectedTemplatingItemId,
  setSelectedTemplatingItemId,
}: SelectedTemplatingItemProviderProps) => {
  return (
    <SelectedTemplatingItemContext.Provider
      value={{
        selectedTemplatingItemId: selectedTemplatingItemId,
        setSelectedTemplatingItemId: setSelectedTemplatingItemId,
      }}
    >
      {children}
    </SelectedTemplatingItemContext.Provider>
  );
};

export const useSelectedTemplatingItem = () => {
  const selectedTemplatingItemContext = useContext(
    SelectedTemplatingItemContext,
  );

  const beforeSelectedTemplatingItemChangeEvent = useEventHook({
    key: 'before-selected-templating-item-change',
  });

  const select = useCallback(
    ({
      template: templatingItemDetailRaw,
    }: {
      template: TemplatingItemDetailRawData;
    }) => {
      return ModelUtils.templatingItemDetailFromRawData(
        templatingItemDetailRaw,
      );
    },
    [],
  );

  const selectedTemplatingItemQuery = apiHooks.useAPIQuery(
    SELECTED_TEMPLATING_ITEM_QUERY_KEY,
    {
      id: selectedTemplatingItemContext.selectedTemplatingItemId ?? '',
    },
    {
      select,
      enabled: selectedTemplatingItemContext.selectedTemplatingItemId !== null,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: setSelectedTemplatingItemId } = useMutation({
    mutationFn: async (id: string | null) => {
      selectedTemplatingItemContext.setSelectedTemplatingItemId(id);
    },
    onMutate: async () => {
      await beforeSelectedTemplatingItemChangeEvent.trigger();
    },
  });

  const isSelected = (templatingItem: TemplatingItem) => {
    return (
      selectedTemplatingItemContext.selectedTemplatingItemId ===
      templatingItem.id
    );
  };

  const { error: selectedTemplatingItemError } = selectedTemplatingItemQuery;

  const templatingItemNotFound = isNotFoundError(selectedTemplatingItemError);

  return {
    selectedTemplatingItemId:
      selectedTemplatingItemContext.selectedTemplatingItemId,
    selectedTemplatingItemQuery,
    setSelectedTemplatingItemId,
    isSelected,
    templatingItemNotFound,
    beforeSelectedTemplatingItemChange:
      beforeSelectedTemplatingItemChangeEvent.on,
  };
};

function isNotFoundError(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 404;
}
